import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ActivityModal, ActivityPreset } from '@components/activity/ActivityModal';

/**
 * Modal de atividade global: qualquer tela (dashboard, kanban, calendário, lista, sino) abre a
 * mesma janela de criar/editar. `version` incrementa a cada alteração salva — as telas o
 * incluem nas dependências do carregamento para se atualizarem sozinhas.
 */
interface ActivityModalContextType {
  openActivity: (id: number) => void;
  openNewActivity: (preset?: ActivityPreset) => void;
  /** Incrementa quando uma atividade é criada, alterada, concluída ou excluída. */
  version: number;
}

const ActivityModalContext = createContext<ActivityModalContextType | undefined>(undefined);

type ModalState = { mode: 'edit'; id: number } | { mode: 'create'; preset?: ActivityPreset } | null;

export function ActivityModalProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ModalState>(null);
  const [version, setVersion] = useState(0);

  const openActivity = useCallback((id: number) => setState({ mode: 'edit', id }), []);
  const openNewActivity = useCallback((preset?: ActivityPreset) => setState({ mode: 'create', preset }), []);
  const close = useCallback(() => setState(null), []);
  const changed = useCallback(() => setVersion((v) => v + 1), []);

  const value = useMemo(() => ({ openActivity, openNewActivity, version }), [openActivity, openNewActivity, version]);

  return (
    <ActivityModalContext.Provider value={value}>
      {children}
      {state && (
        <ActivityModal
          key={state.mode === 'edit' ? `edit-${state.id}` : 'create'}
          activityId={state.mode === 'edit' ? state.id : undefined}
          preset={state.mode === 'create' ? state.preset : undefined}
          onClose={close}
          onChanged={changed}
        />
      )}
    </ActivityModalContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActivityModal(): ActivityModalContextType {
  const context = useContext(ActivityModalContext);
  if (context === undefined) {
    throw new Error('useActivityModal deve ser usado dentro de um ActivityModalProvider');
  }
  return context;
}
